import { MessageBarType } from "@fluentui/react";
import { useState } from "react";
import { IDataResult, ResponseStatus, IResult } from "../../common/models/Result";
import { useForm } from "../../common/viewmodels/DefaultVM";
import { DataUtils } from "../../common/views/DataUtils";
import { Utils } from "../../common/views/Utils";
import { UploadPictureModel } from "../../pictures/models/PictureModel";
import { createProduct, EditProductModel } from "../../products/models/ProductModel";
import { WarehouseModel } from "../../products/models/WarehouseModel";
import {
    ApproveOrderModel, ChangeWebsiteOrderModel, createEditOrder,
    EditOrderModel, OrderModel, OrderStatus
} from "../models/OrderModel";
import { OrderAdminService } from "../services/OrderAdminService";

export function useOrderDetailVM(id: number) {
    const orderService = new OrderAdminService();

    const [order, setOrder] = useState<OrderModel | null>(null);
    const [loading, setLoading] = useState(false);
    const [showPicture, setShowPicture] = useState(false);
    const [showWarehouseList, setShowWarehouseList] = useState(false);

    const {
        form: editForm,
        setForm: setEditForm,
        resetForm: resetEditForm,
        setField: setEditField,
        errorMessages,
        setErrorMessages,
        summaryText,
        setSummaryText,
        summaryType,
        setSummaryType
    } = useForm<EditOrderModel>(createEditOrder());

    const {
        form: productForm,
        setForm: setProductForm,
        resetForm: resetProductForm,
        setField: setProductField
    } = useForm<EditProductModel>(createProduct());

    const {
        form: approveForm,
        setForm: setApproveForm,
        resetForm: resetApproveForm,
        setField: setApproveField
    } = useForm<ApproveOrderModel>({
        id: 0,
        status: OrderStatus.request
    });

    const {
        form: websiteForm,
        setForm: setWebsiteForm,
        resetForm: resetWebsiteForm,
        setField: setWebsiteField
    } = useForm<ChangeWebsiteOrderModel>({
        id: 0,
        websiteId: undefined,
        earningPointRate: 0
    });

    const get = () => {
        setLoading(true);
        setOrder(null);
        const request = orderService.get(id);
        DataUtils.onRequest(request, onGetResult);
    }

    const onGetResult = (result: IDataResult) => {
        const order = result.data as OrderModel | null | undefined;

        if (order != null) {
            setOrder(order);

            setEditForm({
                id: order.id,
                price: order.price,
                qty: order.qty,
                rate: order.rate,
                domesticShipping: order.domesticShipping,
                chargeWeight: order.chargeWeight,
                shippingRate: order.shippingRate,
                note: order.note
            });

            setProductForm({
                id: order.id,
                name: order.product.name,
                description: order.product.description,
                link: order.product.link,
                weight: order.product.weight,
                dimension: order.product.dimension,
                note: order.product.note
            });

            setApproveForm({
                id: order.id,
                status: order.status,
                reason: order.reason,
                tracking: order.tracking,
                note: order.note
            });

            setWebsiteForm({
                id: order.id,
                websiteId: order.product.website?.id,
                earningPointRate: order.earningPointRate
            });
        }

        setLoading(false);
    }

    const edit = () => {
        setLoading(true);
        const request = orderService.editPrice(editForm);
        DataUtils.onRequest(request, onEditResult);
    }

    const onEditResult = (result: IDataResult) => {
        if (!result.errorMessages) result.errorMessages = {};

        if (result.status === ResponseStatus.success) {
            resetEditForm();
            get();
        }

        setLoading(false);

        const res = result as IResult
        if (res) { setErrorMessage(res); }
    }

    const editProduct = () => {
        setLoading(true);
        const request = orderService.editProduct(id, productForm);
        DataUtils.onRequest(request, onEditProductResult);
    }

    const onEditProductResult = (result: IDataResult) => {
        if (!result.errorMessages) result.errorMessages = {};

        if (result.status === ResponseStatus.success) {
            resetProductForm();
            get();
        }

        setLoading(false);

        const res = result as IResult
        if (res) { setErrorMessage(res); }
    }

    const approve = () => {
        const onResult = (result: IDataResult) => {
            if (result.status === ResponseStatus.success) {
                resetApproveForm();
                get();
            }

            setLoading(false);
        }

        const request = orderService.approve(approveForm);
        DataUtils.onRequest(request, onResult);
    }

    const changeWebsite = () => {
        const onResult = (result: IDataResult) => {
            if (result.status === ResponseStatus.success) {
                resetWebsiteForm();
                get();
            }

            setLoading(false);
        }

        const request = orderService.changeWebiste(websiteForm);
        DataUtils.onRequest(request, onResult);
    }

    const unAssignWarehouse = () => {
        if (window.confirm("Are you sure to unassign warehouse?")) {
            const request = orderService.unassignWarehouse(id);
            const onResult = (result: IDataResult) => {
                if (result.status === ResponseStatus.success) {
                    get();
                    setShowWarehouseList(false);
                }
            }

            DataUtils.onRequest(request, onResult);
        }
    }

    const assignWarehouse = (warehouse: WarehouseModel) => {
        const msg = "Are you sure to change warehouse to " + warehouse.name + "?";

        if (window.confirm(msg)) {
            const request = orderService.changeWarehouse(id, warehouse.id);
            const onResult = (result: IDataResult) => {
                if (result.status === ResponseStatus.success) {
                    get();
                    setShowWarehouseList(false);
                }
            }

            DataUtils.onRequest(request, onResult);
        }
    }

    const addPicture = (picture: UploadPictureModel) => {
        const onResult = (result: IDataResult) => {
            if (result.status === ResponseStatus.success) {
                setShowPicture(false);
                get();
            }
        }

        let requestForm = new FormData();
        Utils.buildFormDataProperties(requestForm, picture, [], "");

        const request = Utils.postFormData(requestForm, orderService.RouteCreatePicture(id));
        DataUtils.onRequest(request, onResult);
    }

    const setErrorMessage = (result: IResult) => {
        setErrorMessages(result.errorMessages);
        setSummaryText(result.summaryText ?? "");
        setSummaryType(result.summaryType ?? MessageBarType.info);
    }

    return {
        order,

        get,
        edit,
        editProduct,
        approve,
        changeWebsite,
        unAssignWarehouse,
        assignWarehouse,
        addPicture,

        loading,
        showPicture,
        setShowPicture,
        showWarehouseList,
        setShowWarehouseList,

        editForm,
        setEditForm,
        resetEditForm,
        setEditField,

        productForm,
        setProductForm,
        resetProductForm,
        setProductField,

        approveForm,
        setApproveForm,
        resetApproveForm,
        setApproveField,

        websiteForm,
        setWebsiteForm,
        resetWebsiteForm,
        setWebsiteField,

        errorMessages,
        summaryText,
        summaryType
    }
}